import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  catchError,
  filter,
  Observable,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { LoginService } from '../services/auth/login.service';
import { MessageService } from 'primeng/api';
import { ErrorMessageService } from '../services/error-message/error-message.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: Subject<any> = new Subject<any>();

  constructor(
    private loginService: LoginService,
    private toastService: MessageService,
    private errorMessageService: ErrorMessageService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const refreshToken = this.loginService.getRefreshToken();

    const excludedEndpoints = [
      '/auth/login',
      '/auth/refresh-token',
      '/auth/revoke-token',
    ];

    if (excludedEndpoints.some((url) => request.url.includes(url))) {
      if (
        ['/auth/refresh-token', '/auth/revoke-token'].some((url) =>
          request.url.includes(url)
        )
      ) {
        request = request.clone({
          setHeaders: {
            'Refresh-Token': refreshToken ?? '',
          },
        });
      }
      return next.handle(request);
    }
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((result) => result === true),
        take(1),
        switchMap(() => next.handle(this.addToken(request)))
      );
    } else {
      return next.handle(this.addToken(request)).pipe(
        catchError((error: any) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(request, next);
          } else {
            this.getErrorResponse(error);
          }
          throw error;
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.loginService.getAccessToken();
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request;
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.loginService.refreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(true);
          return next.handle(this.addToken(request));
        }),
        catchError((err: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);

          this.toastService.add({
            severity: 'error',
            summary: 'Your Session Expired.Please Login Again',
          });
          this.loginService.logout();

          throw err;
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((result) => result === true),
        take(1),
        switchMap(() => next.handle(this.addToken(request)))
      );
    }
  }

  private getErrorResponse(error: any): void {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      const message = "Your access to some features has been restricted. If you think this is an error, please contact the administrator."
      localStorage.setItem('forbidden', message);
      return;
    }
    localStorage.removeItem('forbidden');
    if (error) {
      return this.toastService.add({
        severity: 'error',
        detail: this.errorMessageService.getErrorMessage(error),
      });
    }
  }
}
